/**
 * The PORTFOLIO rollup: GET /api/admin/crm/projects/summary, which answers with
 * the same aggregates the per-project summary reports but for a whole filtered
 * set of projects at once. It is what the register header prints above the list.
 *
 * A sibling of `projects.ts` rather than more lines inside it. That file was
 * already at the point where the next feature would push it past the size the
 * house rules allow, and a portfolio rollup is a describable responsibility of
 * its own: one endpoint, one shape, one fetcher. It is deliberately NOT a
 * `projects/` folder with an `index.ts` - this repo has been bitten before by a
 * file-to-folder move emptying the SPA silently - and `projects.ts` re-exports
 * both names below, so every existing `from "./projects.js"` import keeps
 * resolving and a caller may use either path.
 *
 * Every field here has a twin in `ProjectSummary`, and the API keeps the two
 * agreeing figure for figure (`crm-projects/lib/portfolio.ts` states the rules
 * once and its test pins them). Widening one side means widening the other.
 */
import { api } from "./api.js";

/**
 * What GET /admin/crm/projects/summary answers with - a mirror of the API's
 * `PortfolioSummary`.
 *
 * Not one field is nullable, and that is the server's doing rather than an
 * omission here: the handler walks the filtered project set and accumulates
 * plain numbers, so an empty set answers with zeros in every field instead of a
 * thinner body. A screen therefore never has to distinguish "no projects" from
 * "the numbers did not arrive" - the second one is a failed request, not a
 * partial answer.
 *
 * `budgetCents` sums only the projects that HAVE a budget; a project with a null
 * budget contributes nothing and is not counted as zero, because "nobody set a
 * budget" and "the budget is nothing" are different claims and only the second
 * can be overspent. `overBudgetCount` is decided in cents on the server, per
 * project, and must not be re-derived here from percentages.
 *
 * `activeCount` counts `in_progress` and nothing else - `not_started` and
 * `on_hold` are open but nobody is working on them, and a header promising "12
 * active" while ten are parked would be read as a workload figure.
 */
export interface ProjectPortfolio {
  projectCount: number;
  activeCount: number;
  overBudgetCount: number;
  budgetCents: number;
  expenseCents: number;
  laborCostCents: number;
  /** Labour plus expenses, and nothing else. Invoiced money is what the client
   *  was ASKED for, not what the work cost us, so it is not part of spend. */
  spentCents: number;
  invoicedCents: number;
  totalMinutes: number;
  billableMinutes: number;
  /**
   * The same three-way split the project page makes, with the same boundary:
   * BILLABLE is chargeable work, BILLED is billable work already on an invoice,
   * UNBILLED is the remainder. A non-billable minute belongs to neither of the
   * last two. The server clamps unbilled per project before adding it up, so
   * this total is the sum of what the project pages show.
   */
  billedMinutes: number;
  unbilledMinutes: number;
  overdueTasks: number;
}

/**
 * The rollup for one filtered set.
 *
 * The server accepts every NARROWING filter the register does - status, client,
 * the deadline window, tag, member and the archive - out of the one builder both
 * endpoints share (`crm-projects/lib/projectFilters.ts`, `portfolioQuery`). What
 * it does not accept is the search term, and its query schema is a plain
 * `z.object` - so `q` is not refused, it is STRIPPED, and the answer comes back
 * looking like a filtered one. Read off `projectFilters.ts`, not assumed: only a
 * malformed value in one of the fields it does take produces the 400. Nothing
 * downstream will catch a filter this endpoint cannot apply,
 * which is why the caller has to be the one that knows: the params passed here
 * must be the SAME filters the list below the header is showing, and a filter
 * the endpoint has no answer for (the search box) means the header has to fall
 * silent rather than print a total over a different set. A header computed over
 * every project while the list shows one client's is not a summary of what the
 * operator is looking at, it is a different question answered in the same box.
 */
export const getProjectPortfolio = (params?: Record<string, string | undefined>) =>
  api.get<ProjectPortfolio>(`/admin/crm/projects/summary${portfolioQs(params)}`);

/** Local rather than shared with `projects.ts`: every parameter this endpoint
 *  takes is a string (ids, a status, two ISO instants, a tag, an archive mode),
 *  so widening the signature to numbers would advertise paging this route does
 *  not have - a rollup that covered page one would be a lie told in currency. */
function portfolioQs(params?: Record<string, string | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}
